import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchRestaurantSettings,
  type FetchRestaurantResponse,
  type RestaurantPreferences,
  type RestaurantSettings,
} from "@/lib/api/settings";

export const RESTAURANT_SETTINGS_QUERY_KEY = ["settings", "restaurant"] as const;

type UseRestaurantSettingsResult = {
  settings: RestaurantSettings | null;
  preferences: RestaurantPreferences | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<FetchRestaurantResponse | undefined>;
  invalidate: () => Promise<void>;
};

export const useRestaurantSettings = (): UseRestaurantSettingsResult => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: RESTAURANT_SETTINGS_QUERY_KEY,
    queryFn: ({ signal }) => fetchRestaurantSettings(signal),
    staleTime: 60_000,
  });

  const restaurant = query.data?.restaurant ?? null;

  const preferences = useMemo<RestaurantPreferences | null>(() => {
    if (!restaurant) {
      return null;
    }
    return restaurant.preferences ?? null;
  }, [restaurant]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: RESTAURANT_SETTINGS_QUERY_KEY });
  };

  const refetch = async () => {
    const result = await query.refetch();
    return result.data;
  };

  return {
    settings: restaurant,
    preferences,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch,
    invalidate,
  };
};
